import React, { useEffect, useState } from 'react';
import { Warehouse } from 'lucide-react';
import { supabase } from '../../../lib/supabase';
import {
  WAREHOUSE_FIELD_LABELS,
  cleanWarehouseInfo,
  type DealWarehouseInfo,
} from '../../../lib/deals/warehouseFields';

/**
 * Indicazioni per il magazzino scritte sull'offerta (deals.warehouse_info),
 * mostrate a chi gestisce o prepara l'ordine nato da quell'offerta.
 * Si legge dall'offerta, non si copia sull'ordine: una sola fonte, e
 * l'ordine vede subito una correzione fatta sull'offerta.
 *
 * Passare `dealId` se gia' noto, altrimenti `orderId`. Nessuna offerta,
 * nessun valore o colonna assente (migration 20261001 non applicata):
 * non mostra niente.
 */
interface DealWarehouseInfoCardProps {
  dealId?: string | null;
  orderId?: string | null;
  className?: string;
}

const FIELDS = ['urgency', 'payment_status', 'packaging_type', 'warehouse_notes'] as const;

export function DealWarehouseInfoCard({ dealId, orderId, className = '' }: DealWarehouseInfoCardProps) {
  const [info, setInfo] = useState<DealWarehouseInfo | null>(null);

  useEffect(() => {
    setInfo(null);
    if (!dealId && !orderId) return;
    let cancelled = false;
    (async () => {
      let id = dealId ?? null;
      if (!id && orderId) {
        const { data } = await supabase.from('orders').select('deal_id').eq('id', orderId).maybeSingle();
        id = (data as { deal_id: string | null } | null)?.deal_id ?? null;
      }
      if (!id || cancelled) return;
      const { data, error } = await supabase.from('deals').select('warehouse_info').eq('id', id).maybeSingle();
      if (cancelled || error) return;
      setInfo(cleanWarehouseInfo((data as { warehouse_info: DealWarehouseInfo | null } | null)?.warehouse_info));
    })();
    return () => { cancelled = true; };
  }, [dealId, orderId]);

  if (!info) return null;

  return (
    <section className={`space-y-2 ${className}`}>
      <h3 className="flex items-center gap-1.5 text-sm font-medium text-[#37352F] dark:text-white">
        <Warehouse className="h-4 w-4 text-[#787774] dark:text-gray-400" />
        Indicazioni per il magazzino
      </h3>
      <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-lg bg-[rgba(55,53,47,0.04)] dark:bg-gray-800 px-3 py-2.5 text-sm">
        {FIELDS.filter(k => info[k]).map(k => (
          <React.Fragment key={k}>
            <dt className="text-[#787774] dark:text-gray-400">{WAREHOUSE_FIELD_LABELS[k]}</dt>
            <dd className="text-[#37352F] dark:text-white whitespace-pre-line">{info[k]}</dd>
          </React.Fragment>
        ))}
      </dl>
    </section>
  );
}

export default DealWarehouseInfoCard;
